import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractValue } from '@/utils/rxjs/helpers/rxjs.helpers';
import { uniqueFilter } from '@/utils/toolkit/array/unique-filter.utils';
import { groupBy } from '@/utils/toolkit/object/group-by.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, defer, NEVER, type Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { sseTurquoiseColor } from '../../../constants/color';
import type {
    BusinessIntelligenceDataPoint,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from '../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { isDataPointMeaningfulInChart } from '../../../providers/business-intelligence/business-intelligence.utils';
import { HighchartsStaticProvider } from '../../../providers/highcharts/highcharts.static.provider';
import { HighchartsChartComponent } from '../../highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';
import { BusinessIntelligenceContinuumType } from '../business-intelligence-representation-data.interface';

export interface BusinessIntelligenceBarChartComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    measureCode: BusinessIntelligenceMeasureCode;
    categorizationDimensionCode: BusinessIntelligenceDimensionCode;
    segmentationDimensionCode: BusinessIntelligenceDimensionCode;
    areSeriesFormattedForColor: boolean;
    keepZeros: boolean;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-bar-chart.scss'],
    selector: 'business-intelligence-bar-chart',
    templateUrl: 'business-intelligence-bar-chart.html',
    imports: [HighchartsChartComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligenceBarChartComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligenceBarChartComponentConfig>
    implements OnInit
{
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);

    noData: boolean = false;

    chartConfig$!: Observable<Highcharts.Options>;

    tooltip$ = new BehaviorSubject<{ tooltip: any; content$: Observable<string> } | null>(null);

    private MAX_READABLE_SERIES = 15;

    ngOnInit() {
        this.tooltip$
            .pipe(
                filter(isNotNullish),
                debounceTime(0),
                switchMap(({ tooltip, content$ }) => content$.pipe(map(content => ({ tooltip, content })))),
                untilDestroyed(this)
            )
            .subscribe(({ tooltip, content }) => {
                tooltip.label.attr({
                    text: content
                });
            });

        const tooltip$ = this.tooltip$;

        this.chartConfig$ = combineLatest([this._representationData$, this._config$]).pipe(
            // replace later with proper error handling for wrong configs
            filter(
                ([representationData, config]) =>
                    !!(representationData && Object.keys(representationData.metadata).length && isNotNullish(config))
            ),
            tap(([representationData, config]) => {
                const meaningfulData = representationData.data.filter(isDataPointMeaningfulInChart([config.measureCode], config.keepZeros));
                this.noData = !meaningfulData.length;
            }),
            switchMap(([representationData, config]) => {
                return defer(() => {
                    const meaningfulData = representationData.data.filter(
                        isDataPointMeaningfulInChart([config.measureCode], config.keepZeros)
                    );
                    const categoriesValues = meaningfulData.map(d => d.dimensions[config.categorizationDimensionCode]).filter(uniqueFilter);

                    const categoriesValuesValuesIndexes = categoriesValues
                        .map((category, index) => ({ [category]: index }))
                        .reduce((acc, val) => ({ ...acc, ...val }), {});

                    const categorizationAxisContinuity = representationData.metadata[config.categorizationDimensionCode].continuity;

                    const generateSeriesData = (seriesData: BusinessIntelligenceDataPoint[]) => {
                        return seriesData
                            .slice()
                            .sort(
                                (dp1, dp2) =>
                                    categoriesValuesValuesIndexes[dp1.dimensions[config.categorizationDimensionCode]] -
                                    categoriesValuesValuesIndexes[dp2.dimensions[config.categorizationDimensionCode]]
                            )
                            .map(dp => {
                                return {
                                    x: categorizationAxisContinuity
                                        ? categorizationAxisContinuity.getContinuumValueFromValue$(
                                              categorizationAxisContinuity.continuum === BusinessIntelligenceContinuumType.TIMESTAMP
                                                  ? new Date(dp.dimensions[config.categorizationDimensionCode])
                                                  : dp.dimensions[config.categorizationDimensionCode]
                                          )
                                        : categoriesValuesValuesIndexes[dp.dimensions[config.categorizationDimensionCode]],
                                    y: dp.measures[config.measureCode]
                                };
                            });
                    };

                    let series: any[];
                    let areSeriesMeaningful: boolean;

                    if (config.segmentationDimensionCode) {
                        series = Array.from(
                            groupBy(meaningfulData, dataPoint => dataPoint.dimensions[config.segmentationDimensionCode]).entries()
                        ).map(([seriesCode, values]) => {
                            return {
                                name: representationData.metadata[
                                    config.segmentationDimensionCode
                                ].valuesFormatting.formatAsStandaloneText$(seriesCode),
                                color: representationData.metadata[config.segmentationDimensionCode].valuesFormatting.formatAsColor$(
                                    seriesCode
                                ),
                                data: generateSeriesData(values as any)
                            };
                        });
                        areSeriesMeaningful = true;
                    } else if (
                        !representationData.metadata[config.categorizationDimensionCode].continuity &&
                        config.areSeriesFormattedForColor &&
                        meaningfulData.length <= this.MAX_READABLE_SERIES
                    ) {
                        series = meaningfulData.map(dataPoint => {
                            return {
                                name: representationData.metadata[
                                    config.categorizationDimensionCode
                                ].valuesFormatting.formatAsStandaloneText$(dataPoint.dimensions[config.categorizationDimensionCode]),
                                color: representationData.metadata[config.categorizationDimensionCode].valuesFormatting.formatAsColor$(
                                    dataPoint.dimensions[config.categorizationDimensionCode]
                                ),
                                data: generateSeriesData([dataPoint])
                            };
                        });
                        areSeriesMeaningful = true;
                    } else {
                        series = [
                            {
                                name: representationData.metadata[config.measureCode].getName$(),
                                color: sseTurquoiseColor,
                                data: generateSeriesData(meaningfulData)
                            }
                        ];
                        areSeriesMeaningful = false;
                    }

                    const getValueToFormatFromXValue$ = (xValue: number) =>
                        categorizationAxisContinuity
                            ? categorizationAxisContinuity.getValueFromContinuumValue$(xValue)
                            : of(categoriesValues[xValue]);

                    return combineLatestDeepObject(
                        this._highchartsStaticProvider.buildConfig(this._highchartsStaticProvider.COLUMN_MIN_SIZE_5, {
                            chart: {
                                type: 'bar'
                            },
                            boost: {
                                // if necessary, we could work so that the chart works in boost mode.
                                // See that if performance issues arise
                                seriesThreshold: 5000
                            },
                            plotOptions: {
                                bar: {
                                    stacking: 'normal',
                                    shadow: false,
                                    borderWidth: 0,
                                    minPointLength: 10,
                                    maxPointWidth: 80
                                }
                            },
                            xAxis: {
                                labels: {
                                    formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                                        // TODO : handle async updates
                                        return extractValue(
                                            getValueToFormatFromXValue$(this.value as number).pipe(
                                                switchMap(valueToFormat =>
                                                    representationData.metadata[
                                                        config.categorizationDimensionCode
                                                    ].valuesFormatting.formatAsStandaloneText$(valueToFormat)
                                                )
                                            )
                                        );
                                    }
                                },
                                type:
                                    categorizationAxisContinuity &&
                                    categorizationAxisContinuity.continuum === BusinessIntelligenceContinuumType.TIMESTAMP
                                        ? 'datetime'
                                        : categorizationAxisContinuity &&
                                            categorizationAxisContinuity.continuum === BusinessIntelligenceContinuumType.NUMBER
                                          ? 'linear'
                                          : 'category'
                            },
                            yAxis: {
                                title: {
                                    text: super.getMemberName$(config.measureCode, representationData)
                                },
                                labels: {
                                    formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                                        // TODO : handle async updates
                                        // TODO : use grouped text formatting when it will be available
                                        return extractValue(
                                            representationData.metadata[config.measureCode].valuesFormatting.formatAsStandaloneText$(
                                                this.value as number
                                            )
                                        );
                                    }
                                }
                            },
                            tooltip: {
                                formatter(this: TooltipFormatterContext, tooltip: string) {
                                    tooltip$.next({
                                        tooltip,
                                        content$: combineLatest([
                                            representationData.metadata[config.measureCode].valuesFormatting.formatAsStandaloneText$(
                                                this.point.y
                                            ),
                                            getValueToFormatFromXValue$(this.point.x).pipe(
                                                switchMap(valueToFormat =>
                                                    representationData.metadata[
                                                        config.categorizationDimensionCode
                                                    ].valuesFormatting.formatAsStandaloneText$(valueToFormat)
                                                )
                                            )
                                        ]).pipe(map(([pointValue, xValue]) => `${xValue} <br/> ${this.series.name}: <b>${pointValue}</b>`))
                                    });
                                    return;
                                }
                            },
                            legend: {
                                enabled: areSeriesMeaningful
                            },
                            series
                        })
                    );
                }).pipe(
                    catchError(err => {
                        console.error('Error while updating bar chart config ::', err);
                        return NEVER;
                    })
                );
            })
        );
    }

    getExportMembers(config: BusinessIntelligenceBarChartComponentConfig): BusinessIntelligenceMemberCode[] {
        return config.segmentationDimensionCode
            ? [config.categorizationDimensionCode, config.segmentationDimensionCode, config.measureCode]
            : [config.categorizationDimensionCode, config.measureCode];
    }
}
